import React from 'react'

import { useTranslation } from 'language/translator'
import { FormattedWidgetData } from 'types/GlobalWidget'

import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import VisuallyHidden from 'components_new/atoms/VisuallyHidden'

interface ChartDataTableProps {
  formattedData: FormattedWidgetData
  title: string
}

const formatCell = (
  value: string | number | string[] | number[] | null
): string => {
  if (value === null || value === undefined) {
    return '–'
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(', ')
  }

  return String(value)
}

/**
 * The ChartDataTable component renders the data behind a
 * canvas chart as a table that is hidden from sighted users
 * but read by assistive technology, so that the chart's
 * information is available without seeing it.
 */

const ChartDataTable = (props: ChartDataTableProps) => {
  const { formattedData, title } = props
  const { t } = useTranslation()

  const datasets = formattedData.datasets.filter((dataset) => !dataset.hidden)

  if (formattedData.labels.length === 0 || datasets.length === 0) {
    return null
  }

  return (
    <VisuallyHidden component="div">
      <Table>
        <caption>
          {t('widgets', 'chart_data_table_caption', false, {
            title: title || t('global', 'chart')
          })}
        </caption>
        <TableHead>
          <TableRow>
            <TableCell component="th" scope="col">
              {t('widgets', 'chart_data_table_label_column')}
            </TableCell>
            {datasets.map((dataset) => (
              <TableCell component="th" key={dataset.id} scope="col">
                {dataset.unit
                  ? `${dataset.label} (${dataset.unit})`
                  : dataset.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {formattedData.labels.map((label, index) => (
            <TableRow key={`${label.display_label}-${index}`}>
              <TableCell component="th" scope="row">
                {label.display_label}
              </TableCell>
              {datasets.map((dataset) => (
                <TableCell key={dataset.id}>
                  {formatCell(dataset.prettyData[index] ?? null)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </VisuallyHidden>
  )
}

ChartDataTable.displayName = 'ChartDataTable'
export default ChartDataTable
